import { Immutable } from "immer";
import React, { useState } from "react";

import { templates } from "@reactivated";
import type { _FaqBlock as FaqBlockValue } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

/**
 * toppers OfferPage ``faq_details`` assembly: collapses the per-block
 * ``{% for block in page.faq_details %}`` loop in
 * ``cms/offer_page.html`` into a SINGLE ``{% include_react %}`` island. The
 * surrounding ``.offer-page__faq`` / ``.accordion__container`` /
 * ``.accordion__list`` chrome + the ``{% if self.faq_details %}`` guard stay in
 * ``offer_page.html`` -- this island renders only the ``<li>`` items.
 *
 * NO WRAPPER (Fragment): the loop did NOT use ``{% include_block %}`` --
 * it MANUALLY emitted each ``<li>`` server-side, so wagtail's ``w-block-*``
 * wrapper was never applied. The per-block DOM was bespoke; this reproduces it
 * verbatim:
 *
 *     <li class="accordion__list-item">
 *         <a class="accordion">{{ question|safe }}</a>
 *         <div class="accordion-target">{{ answer|safe }}</div>
 *     </li>
 *
 * SUB-FIELDS (``_FaqBlock``, both serialize to ``string | null``):
 * - ``question`` (CharBlock) and ``answer`` (TextBlock): both rendered
 *   with ``|safe`` (NO autoescape), so raw-inject to match exactly -- React text
 *   nodes would HTML-escape entities passed through verbatim.
 *
 * Each item owns its open/close state: clicking the ``<a>`` trigger toggles
 * ``accordion--is-active`` on it and ``accordion-target--is-active`` on its
 * answer. The ``<a>`` carries no ``href`` (legacy relied on a global click
 * handler), so it is exposed as a keyboard-operable ``role="button"`` with
 * ``aria-expanded``. Items start collapsed.
 */
const FaqAccordionItem = (
    props: Immutable<{ value: FaqBlockValue }>,
): React.JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const toggle = () => {
        setIsOpen((open) => !open);
    };
    return (
        <li className="accordion__list-item">
            <a
                className={
                    isOpen ? "accordion accordion--is-active" : "accordion"
                }
                role="button"
                tabIndex={0}
                aria-expanded={isOpen ? "true" : "false"}
                onClick={(event) => {
                    event.preventDefault();
                    toggle();
                }}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggle();
                    }
                }}
                dangerouslySetInnerHTML={{ __html: props.value.question ?? "" }}
            />
            <div
                className={
                    isOpen
                        ? "accordion-target accordion-target--is-active"
                        : "accordion-target"
                }
                dangerouslySetInnerHTML={{ __html: props.value.answer ?? "" }}
            />
        </li>
    );
};

const faqDetailsComponentsMap = {
    faq_block: (block: { value: Immutable<FaqBlockValue> }) => (
        <FaqAccordionItem value={block.value} />
    ),
} as const;

export const OfferPageFaqDetails = (
    props: Immutable<{
        page: Pick<templates.OfferPageTemplate["page"], "faq_details">;
    }>,
): React.JSX.Element => {
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.faq_details.value}
            components={faqDetailsComponentsMap}
        />
    );
};
